"use client"

import addFreeShipping from "@/actions/selection/add-free-shipping"
import { KeepSelection, keepSelection } from "@/actions/selection/keep-selection"
import { removeFreeShipping } from "@/actions/selection/remove-free-shipping"
import IconArrowLeft from "@/app/(site)/_components/Atoms/Icons/IconArrowLeft"
import IconCheck from "@/app/(site)/_components/Atoms/Icons/IconCheck"
import IconChevronDown from "@/app/(site)/_components/Atoms/Icons/IconChevronDown"
import IconParcel from "@/app/(site)/_components/Atoms/Icons/IconParcel"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import Text from "@/app/(site)/_components/Atoms/Text"
import { getCurrentSelectedState } from "@/app/(site)/_components/Modules/Checkout/CartAside/helpers"
import LoadingButton from "@/app/(site)/_components/Modules/Checkout/LoadingButton"
import BillingFields from "@/app/(site)/_components/Modules/Checkout/Views/Information/BillingFields"
import {
    isValidStreetName,
    useValidateEmailDebounce
} from "@/app/(site)/_components/Modules/Checkout/Views/Information/helpers"
import {
    fieldLabel,
    fieldPlaceholder,
    klaviyoFormCodesMap
} from "@/app/(site)/_components/Modules/Checkout/Views/Information/maps"
import {
    formSchema,
    formSchemaWithBillingState,
    formSchemaWithShippingAndBillingState,
    formSchemaWithShippingState
} from "@/app/(site)/_components/Modules/Checkout/Views/Information/validation"
import Skeleton from "@/app/(site)/_components/Modules/Checkout/Views/Skeleton"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import RadioButton from "@/app/(site)/_components/Molecules/Selectors/Radiobutton"
import { Spinner } from "@/app/(site)/_components/Molecules/Spinner"
import TextField from "@/app/(site)/_components/Molecules/TextField"
import { handleShippingAndLanguageSelection } from "@/app/(site)/_components/Organisms/Header/CountryPickerSelection/CountryPickerSelectionItems"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { removeFields } from "@/app/(site)/_utils/transformData/centraData"
import { useCheckoutForm } from "@/app/(site)/contexts/checkout"
import { CustomerInfo } from "@/app/(site)/contexts/checkout/types"
import { restrictedZip } from "@/data/pfas-zipcode-state-restrictions"
import { getStatesByCountryCode } from "@/data/shipping-country-code"
import centraPath from "@/lib/centra-path/centra-path-init"
import { setUserLocaleClient } from "@/lib/cookie/site-cookie/cookieActions.client"
import { isClient } from "@/lib/env-utils/env-utils"
import { parseGooglePlaceResult } from "@/lib/utils/checkout/googlePlaceApi"
import { t } from "@/translations"
import { CheckoutFormData, Country, Fields, StateOption } from "@/types/checkout-page"
import { Err, Ok } from "@/types/generic/Result"
import { Library } from "@googlemaps/js-api-loader"
import { Autocomplete, useLoadScript } from "@react-google-maps/api"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import getUnicodeFlagIcon from "country-flag-icons/unicode"
import { usePathname, useRouter } from "next/navigation"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { match } from "ts-pattern"
import PlaceResult = google.maps.places.PlaceResult

declare global {
    interface Window {
        _klOnsite: any[]
        dataLayer?: Object[]
    }
}

interface InformationProps {
    countries: Country[]
}

const paymentFailedPath = "/payment-failed"
const paymentReturnPath = "/api/payment-result"

const scriptOptions = {
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"] as Library[]
}

const Information = ({ countries }: InformationProps) => {
    const { isLoaded, loadError } = useLoadScript(scriptOptions)

    const [autocomplete, setAutocomplete] =
        React.useState<google.maps.places.Autocomplete | null>(null)
    const contactFormRef = React.useRef<HTMLDivElement | null>(null)
    const billingFormRef = React.useRef<HTMLDivElement | null>(null)
    const [isLoading, setIsLoading] = React.useState(false)
    const [shippingFields, setShippingFields] = React.useState<Fields>({})
    const [billingFields, setBillingFields] = React.useState<Fields>({})
    const [stateFields, setStateFields] = React.useState<StateOption[]>([])
    const timerRef = React.useRef<number | null>(null)
    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""
    const { customerInfo, updateCustomerInfo } = useCheckoutForm()
    const queryClient = useQueryClient()
    const router = useRouter()
    const shippingAddress = customerInfo?.shippingAddress || {}
    const [differentShippingAddress, setDifferentShippingAddress] = React.useState(
        () => customerInfo?.differentShippingAddress || false
    )
    const pathname = usePathname()

    const maybeCentraLocale =
        centraPath.extractValidAndLegalCentraLocaleFromPath(pathname)

    const autoCompleteOptions: google.maps.places.AutocompleteOptions = {
        componentRestrictions: {
            country: customerInfo.shippingAddress.country || ""
        },
        types: ["address"]
    }
    const [inputValue, setInputValue] = React.useState("")

    useEffect(() => {
        setInputValue(customerInfo.shippingAddress.address1 || "")
        trigger("shippingAddress.address1")
    }, [customerInfo.shippingAddress.address1])

    /**
     * this functions is used when a faulty key or other errors occurs from the Autocomplete component
     * when no error is visible via loadError
     */
    const inputRef = useRef<HTMLInputElement>(null)
    const [forceRenderKey, setForceRenderKey] = useState("")
    const handleInputFocus = useCallback((e: { target: { disabled: boolean } }) => {
        if (e.target.disabled) {
            setForceRenderKey(prevKey => prevKey + "x")

            e.target.disabled = false
            setTimeout(() => {
                inputRef?.current?.focus()
            }, 0)
        }
    }, [])

    const onPlaceChanged = () => {
        if (isLoaded && autocomplete) {
            const place: PlaceResult = autocomplete.getPlace()
            const placeAddress = parseGooglePlaceResult(
                place,
                customerInfo.shippingAddress.country
            )
            setInputValue(placeAddress.address1)
            updateCustomerInfo({
                shippingAddress: {
                    ...customerInfo.shippingAddress,
                    address1: placeAddress.address1,
                    zipCode: placeAddress.zipCode,
                    city: placeAddress.city,
                    state: placeAddress.state
                }
            })
            setValue("shippingAddress.address1", placeAddress.address1)
            setValue("shippingAddress.city", placeAddress.city)
            setValue("shippingAddress.zipCode", placeAddress.zipCode)
            setValue("shippingAddress.state", placeAddress.state)

            // Clear errors for all fields that were updated
            clearErrors([
                "shippingAddress.address1",
                "shippingAddress.city",
                "shippingAddress.zipCode",
                "shippingAddress.state"
            ])
        }
    }

    const onLoad = (autocompleteObj: google.maps.places.Autocomplete) => {
        setAutocomplete(autocompleteObj)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("shippingAddress.address1", e.target.value)
        updateCustomerInfo({
            shippingAddress: {
                ...customerInfo.shippingAddress,
                address1: e.target.value
            }
        })
        setInputValue(e.target.value) // Control the input value
    }

    const fieldRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({})

    const {
        register,
        reset,
        handleSubmit,
        formState: { errors, touchedFields },
        setValue,
        setError,
        trigger,
        clearErrors
    } = useForm<CheckoutFormData>({
        defaultValues: {
            paymentMethod: "adyen-drop-in-eu-test", //this seems wrong, should be "adyen-drop-in-eu" not "-test", look into this...
            paymentReturnPage:
                typeof window !== "undefined"
                    ? window.location.origin + paymentReturnPath
                    : "",
            paymentFailedPage:
                typeof window !== "undefined"
                    ? window.location.origin + paymentFailedPath
                    : "",
            shippingAddress,
            billingAddress: customerInfo?.billingAddress || {},
            termsAndConditions: true
        },
        mode: "all",
        reValidateMode: "onChange",
        shouldUnregister: false
    })

    const {
        validKlaviyoEmail,
        callChangePaymentFields,
        validateIsEmailSignedUpToKlaviyo,
        setValidKlaviyoEmail
    } = useValidateEmailDebounce(addFreeShipping, removeFreeShipping)

    const { data } = useQuery({
        queryKey: ["selection"],
        queryFn: () => keepSelection()
    })

    const selectionData = queryClient
        .getQueryCache()
        .find<KeepSelection>({ queryKey: ["selection"] })?.state.data

    React.useEffect(() => {
        if (selectionData) {
            const addressData = (selectionData as { data: KeepSelection }).data
                ?.checkoutApiSelection?.selection

            const shippingAddress = addressData?.shippingAddress || {}
            const address = addressData?.address || {}

            if (addressData) {
                reset({
                    shippingAddress,
                    billingAddress: address
                })
            }
        }
    }, [selectionData])

    React.useEffect(() => {
        if (data) {
            match(data)
                .with(Ok, result => {
                    const email =
                        result.data.checkoutApiSelection?.selection?.shippingAddress
                            ?.email || ""
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

                    if (email === "" || !emailRegex.test(email)) {
                        setValidKlaviyoEmail(false)
                    }
                    if (email) {
                        validateIsEmailSignedUpToKlaviyo(email)
                    }
                })
                .with(Err, error => {
                    return null
                })
                .exhaustive()
        }
    }, [])

    const debouncedCallApi = React.useCallback(
        (data: CustomerInfo) => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
            timerRef.current = window.setTimeout(() => {
                callChangePaymentFields(data)
            }, 300)
        },
        [callChangePaymentFields]
    )

    React.useEffect(() => {
        debouncedCallApi(customerInfo)
        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [customerInfo])

    useEffect(() => {
        // Trigger zip code validation whenever state changes
        if (customerInfo.shippingAddress.country === "US") {
            trigger("shippingAddress.zipCode")
        }
    }, [customerInfo.shippingAddress.state])

    React.useEffect(() => {
        if (data) {
            match(data)
                .with(Ok, data => {
                    const shippingFieldsObject = removeFields(
                        data?.data?.checkoutApiSelection?.paymentFields?.shippingAddress
                    ) as Fields

                    const billingFieldsObject = removeFields(
                        data?.data?.checkoutApiSelection?.paymentFields?.address
                    ) as Fields

                    const shippingStateFields =
                        data?.data?.checkoutApiSelection?.countries?.find(
                            country =>
                                country?.country ===
                                data?.data?.checkoutApiSelection?.location?.country
                        )?.states

                    const currentState =
                        getCurrentSelectedState(
                            data.data?.checkoutApiSelection?.selection.shippingAddress
                                .state,
                            undefined
                        ) || ""

                    updateCustomerInfo({
                        shippingAddress: {
                            country: data?.data?.checkoutApiSelection?.location?.country,
                            state: currentState
                        }
                    })

                    setValue(
                        "shippingAddress.country",
                        data?.data?.checkoutApiSelection?.location?.country as string
                    )
                    setValue("shippingAddress.state", currentState)
                    // If differentShippingAddress is true, set billingAddress state to its billing address state
                    if (differentShippingAddress) {
                        setValue(
                            "billingAddress.state",
                            data?.data?.checkoutApiSelection?.selection?.address?.state
                        )
                    }

                    const shippingAddress =
                        data?.data?.checkoutApiSelection?.selection?.shippingAddress
                    const billingAddress =
                        data?.data?.checkoutApiSelection?.selection?.address
                    const location = data?.data?.checkoutApiSelection?.location

                    updateCustomerInfo({
                        shippingAddress: {
                            firstName: shippingAddress?.firstName,
                            lastName: shippingAddress?.lastName,
                            email: shippingAddress?.email,
                            phoneNumber: shippingAddress?.phoneNumber,
                            address1: shippingAddress?.address1,
                            address2: shippingAddress?.address2,
                            zipCode: shippingAddress?.zipCode,
                            city: shippingAddress?.city,
                            country: location?.country,
                            state: currentState
                        },
                        billingAddress: {
                            firstName: billingAddress?.firstName,
                            lastName: billingAddress?.lastName,
                            email: billingAddress?.email,
                            phoneNumber: billingAddress?.phoneNumber,
                            address1: billingAddress?.address1,
                            address2: billingAddress?.address2,
                            zipCode: billingAddress?.zipCode,
                            city: billingAddress?.city,
                            country: shippingAddress?.country || location?.country,
                            state: currentState
                        }
                    })

                    setStateFields(shippingStateFields || [])
                    setShippingFields(shippingFieldsObject || {})
                    setBillingFields(billingFieldsObject || {})
                })
                .with(Err, error => {
                    toast.error("An error occurred while retrieving cart data")
                })
                .exhaustive()
        }
    }, [data])

    const handleChangeShippingCountry = async (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setIsLoading(true)
        const countryCode = e.target.value
        setValue("shippingAddress.state", "")
        setValue("shippingAddress.country", countryCode)
        setValue("shippingAddress.city", "")
        setValue("shippingAddress.address1", "")
        setValue("shippingAddress.address2", "")
        setValue("shippingAddress.zipCode", "")
        updateCustomerInfo({
            shippingAddress: {
                ...customerInfo?.shippingAddress,
                country: countryCode,
                city: "",
                address1: "",
                address2: "",
                zipCode: ""
            }
        })
        const newPath = await handleShippingAndLanguageSelection(
            null,
            countryCode,
            null,
            undefined
        )

        setUserLocaleClient({ path: `/${newPath.split("/")[1]}`, countryCode })
        if (isClient) {
            if (newPath === window.location.pathname) {
                location.reload()
            } else {
                window.location.href = newPath
            }
        }
    }

    const onSubmit = async (data: CheckoutFormData) => {
        setIsLoading(true)
        let validationResult

        const billingCountryCode =
            customerInfo.billingAddress.country ||
            customerInfo.shippingAddress.country ||
            ""

        const differentShippingAddress = customerInfo.differentShippingAddress
        // Use different validation schemas based on if we have states or not.
        const billingStates = getStatesByCountryCode(countries, billingCountryCode)
        if (shippingFields["state"]?.required && billingStates.length > 0) {
            validationResult = formSchemaWithShippingAndBillingState(
                differentShippingAddress
            ).safeParse(data)
        } else if (shippingFields["state"]?.required) {
            validationResult = formSchemaWithShippingState.safeParse(data)
        } else if (billingStates.length > 0) {
            validationResult = formSchemaWithBillingState.safeParse(data)
        } else {
            validationResult = formSchema(differentShippingAddress).safeParse(data)
        }

        if (customerInfo.pfasRestrictedProduct) {
            router.push(`/${maybeCentraLocale}/checkout`)
            setIsLoading(false)
            return
        }

        if (!validationResult.success) {
            const errorFields = [] as {
                path: string
                topPosition: number
                fieldRef: HTMLDivElement
            }[]

            validationResult.error.issues.forEach(issue => {
                const path = issue.path.join(".")
                setError(path as keyof CheckoutFormData, {
                    type: "manual",
                    message: issue.message
                })
                const fieldRef = fieldRefs.current[path]
                if (fieldRef) {
                    const rect = fieldRef.getBoundingClientRect()
                    const topPosition = rect.top + window.scrollY
                    errorFields.push({ path, topPosition, fieldRef })
                }
            })

            // Sort the error fields by their position from top
            errorFields.sort((a, b) => a.topPosition - b.topPosition)

            // Scroll to the field that is most above
            if (errorFields.length > 0) {
                const topFieldRef = errorFields[0].fieldRef
                topFieldRef.scrollIntoView({ behavior: "smooth", block: "center" })
            }

            // Determine if there are any issues related to shippingAddress
            const hasShippingAddressIssues = validationResult.error.issues.some(issue =>
                issue.path.includes("shippingAddress")
            )

            // Determine if there are any issues related to billingAddress
            const hasBillingAddressIssues = validationResult.error.issues.some(issue =>
                issue.path.includes("billingAddress")
            )

            // If differentShippingAddress is true, validate both shippingAddress and billingAddress
            if (customerInfo.differentShippingAddress) {
                if (!hasShippingAddressIssues && !hasBillingAddressIssues) {
                    router.push(`/${maybeCentraLocale}/checkout/shipping`)
                    return
                }
            } else {
                // If differentShippingAddress is false, validate only shippingAddress
                if (!hasShippingAddressIssues) {
                    router.push(`/${maybeCentraLocale}/checkout/shipping`)
                    return
                }
            }
        }
        if (validationResult.success) {
            router.push(`/${maybeCentraLocale}/checkout/shipping`)
        }

        setIsLoading(false)
    }

    const handleDifferentBillingAddress = useCallback(() => {
        updateCustomerInfo({
            differentShippingAddress: !differentShippingAddress,
            billingAddress: {
                ...customerInfo.billingAddress,
                country: customerInfo.shippingAddress.country,
                state: ""
            }
        })
        setDifferentShippingAddress(!differentShippingAddress)
    }, [updateCustomerInfo, differentShippingAddress, customerInfo.billingAddress])

    const triggerKlaviyoEmailForm = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, langCode: string) => {
            e.preventDefault()

            window._klOnsite = window._klOnsite || []

            const formCode =
                klaviyoFormCodesMap.get(langCode) || klaviyoFormCodesMap.get("en")

            window._klOnsite.push(["openForm", formCode])
        },
        []
    )

    if (!data) return <Skeleton data={data} />

    return (
        <>
            <div className="klaviyo-form-Rzsj8c"></div>
            {data && (
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-48 md:mb-50" ref={contactFormRef}>
                        <StoryText size="size2" className="mb-24">
                            {t(langCode, "CONTACT INFORMATION")}
                        </StoryText>
                        <div className="mb-16">
                            <Text
                                size="small"
                                weight="regular"
                                className={`mb-8 ${errors.shippingAddress?.email?.ref ? "text-red-500" : "text-black"}`}
                            >
                                <label htmlFor="email">
                                    {!errors?.shippingAddress?.email &&
                                        t(langCode, fieldLabel.get("Email") ?? "")}
                                    {errors?.shippingAddress?.email &&
                                        t(
                                            langCode,
                                            errors?.shippingAddress?.email.message ?? ""
                                        )}
                                </label>
                            </Text>
                            <div
                                ref={el =>
                                    (fieldRefs.current["shippingAddress.email"] = el)
                                }
                            >
                                <TextField
                                    type={"text"}
                                    {...register("shippingAddress.email")}
                                    onChange={e => {
                                        const email = e.target.value
                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

                                        validateIsEmailSignedUpToKlaviyo(email)
                                        if (emailRegex.test(email) || email === "") {
                                            setValue("shippingAddress.email", email, {
                                                shouldValidate: true
                                            })
                                            updateCustomerInfo({
                                                shippingAddress: {
                                                    ...customerInfo?.shippingAddress,
                                                    email: email
                                                }
                                            })
                                        }

                                        // If not a valid email, remove voucher
                                        if (!emailRegex.test(email) || email === "") {
                                            setValue("shippingAddress.email", email, {
                                                shouldValidate: true
                                            })
                                            updateCustomerInfo({
                                                shippingAddress: {
                                                    ...customerInfo?.shippingAddress,
                                                    email: ""
                                                }
                                            })
                                            setValidKlaviyoEmail(false)
                                        }
                                        clearErrors("shippingAddress.email")
                                    }}
                                    onPaste={e => {
                                        e.preventDefault()
                                        const email = e.clipboardData.getData("text")
                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

                                        if (emailRegex.test(email) || email === "") {
                                            setValue("shippingAddress.email", email, {
                                                shouldValidate: true
                                            })
                                            updateCustomerInfo({
                                                shippingAddress: {
                                                    ...customerInfo?.shippingAddress,
                                                    email: email
                                                }
                                            })
                                        }

                                        validateIsEmailSignedUpToKlaviyo(email)
                                    }}
                                    placeholder={t(
                                        langCode,
                                        fieldPlaceholder.get("Email") ?? ""
                                    )}
                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                        validateIsEmailSignedUpToKlaviyo(e.target.value)
                                    }
                                    aria-label={t(langCode, "Email")}
                                    className={
                                        errors.shippingAddress?.email?.ref
                                            ? "border-red-500"
                                            : ""
                                    }
                                    theme="light"
                                    name="shippingAddress.email"
                                    id="email"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-10">
                            {validKlaviyoEmail === true ? (
                                <>
                                    <IconCheck className="h-24 w-24 text-[#008000]" />
                                    <StoryText size="size4">
                                        {t(langCode, "Free shipping for POC members.")}
                                        {"*"}
                                    </StoryText>
                                </>
                            ) : validKlaviyoEmail === false ? (
                                <>
                                    <IconParcel className="h-24 w-24 text-[#000E88]" />
                                    <StoryText
                                        size="size4"
                                        className="mt-5 text-[#000E88]"
                                    >
                                        {t(langCode, "Free shipping for POC members.")}
                                        {"*"}
                                        <button
                                            type="button"
                                            className="klaviyo_form_trigger underline"
                                            onClick={e =>
                                                triggerKlaviyoEmailForm(e, langCode)
                                            }
                                            onKeyDown={e => {
                                                // Prevent the form from submitting or button click on 'Enter'
                                                if (e.key === "Enter") {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                }
                                            }}
                                        >
                                            {t(langCode, "Sign up now")}
                                        </button>
                                    </StoryText>
                                </>
                            ) : null}
                        </div>
                    </div>
                    <div className="mb-48 grid grid-cols-2 gap-24">
                        <div className="col-span-2">
                            <StoryText size="size2" className="mb-0 md:mb-24">
                                {t(langCode, "SHIPPING ADDRESS")}
                            </StoryText>
                        </div>
                        <div className="col-span-2">
                            {/* LABEL */}
                            <Text
                                size="small"
                                weight="regular"
                                className={`mb-8 ${errors.shippingAddress?.country?.ref ? "text-red-500" : "text-black"}`}
                            >
                                <label htmlFor="shippingAddress.country">
                                    {t(langCode, "Country")}
                                    {t(
                                        langCode,
                                        errors?.shippingAddress?.country?.message ?? ""
                                    )}
                                </label>
                            </Text>
                            {/* SELECT */}
                            <div
                                className="relative w-full"
                                ref={el =>
                                    (fieldRefs.current["shippingAddress.country"] = el)
                                }
                            >
                                <select
                                    className={`w-full appearance-none rounded-full border py-16 pl-50 pr-24 ${errors.shippingAddress?.country ? "border-red-500" : ""}`}
                                    value={customerInfo?.shippingAddress?.country}
                                    onChange={async e => {
                                        await handleChangeShippingCountry(e)
                                    }}
                                >
                                    {countries &&
                                        countries.map((country, index) => (
                                            <option
                                                key={index}
                                                value={country.country ?? ""}
                                            >
                                                {country.name}
                                            </option>
                                        ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-24 text-gray-700">
                                    <span>
                                        {customerInfo?.shippingAddress?.country ? (
                                            getUnicodeFlagIcon(
                                                customerInfo.shippingAddress.country.toUpperCase()
                                            )
                                        ) : (
                                            <span>No country specified</span>
                                        )}
                                    </span>
                                </div>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-24 text-gray-700">
                                    {isLoading ? (
                                        <Spinner />
                                    ) : (
                                        <IconChevronDown className="ml-4 h-20 w-20" />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            {/* FIRST NAME */}

                            <div>
                                {/* LABEL */}
                                <Text size="small" weight="regular" className="mb-8">
                                    <label
                                        htmlFor="firstName"
                                        className={`${errors?.shippingAddress?.firstName?.ref ? "text-red-500" : "text-black"}`}
                                    >
                                        {!errors?.shippingAddress?.firstName &&
                                            t(
                                                langCode,
                                                fieldLabel.get("Firstname") ?? ""
                                            )}
                                        {errors?.shippingAddress?.firstName &&
                                            t(
                                                langCode,
                                                errors?.shippingAddress?.firstName
                                                    .message ?? ""
                                            )}
                                    </label>
                                </Text>
                                <div
                                    ref={el =>
                                        (fieldRefs.current["shippingAddress.firstName"] =
                                            el)
                                    }
                                >
                                    <TextField
                                        {...register("shippingAddress.firstName")}
                                        placeholder={t(
                                            langCode,
                                            fieldPlaceholder.get("Firstname") ?? ""
                                        )}
                                        className={`${errors?.shippingAddress?.firstName ? "border-red-500" : ""}`}
                                        theme="light"
                                        id="firstName"
                                        name="shippingAddress.firstName"
                                        aria-label={t(langCode, "First name")}
                                        onChange={e => {
                                            updateCustomerInfo({
                                                shippingAddress: {
                                                    ...customerInfo?.shippingAddress,
                                                    firstName: e.target.value
                                                }
                                            })

                                            setValue(
                                                "shippingAddress.firstName",
                                                e.target.value
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            <Text size="small" weight="regular" className="mb-8">
                                <label
                                    htmlFor="lastName"
                                    className={` ${errors?.shippingAddress?.lastName ? "text-red-500" : "text-black"}`}
                                >
                                    {!errors?.shippingAddress?.lastName &&
                                        t(langCode, fieldLabel.get("Lastname") ?? "")}
                                    {errors?.shippingAddress?.lastName &&
                                        t(
                                            langCode,
                                            errors?.shippingAddress?.lastName?.message ??
                                                ""
                                        )}
                                </label>
                            </Text>
                            <div
                                ref={el =>
                                    (fieldRefs.current["shippingAddress.lastName"] = el)
                                }
                            >
                                <TextField
                                    {...register("shippingAddress.lastName")}
                                    placeholder={t(
                                        langCode,
                                        fieldPlaceholder.get("Lastname") ?? ""
                                    )}
                                    theme="light"
                                    id="lastName"
                                    aria-label={t(langCode, "Last name")}
                                    name="shippingAddress.lastName"
                                    className={`${errors?.shippingAddress?.lastName ? "border-red-500" : " "}`}
                                    onChange={e => {
                                        updateCustomerInfo({
                                            shippingAddress: {
                                                ...customerInfo?.shippingAddress,
                                                lastName: e.target.value
                                            }
                                        })
                                        setValue(
                                            "shippingAddress.lastName",
                                            e.target.value
                                        )
                                    }}
                                />
                            </div>
                        </div>
                        {shippingFields &&
                            Object.keys(shippingFields).map((fieldName, index) => {
                                const field = shippingFields[fieldName]
                                if (fieldName === "state" && field.required) {
                                    return (
                                        <div className="col-span-2" key={index}>
                                            <Text
                                                size="small"
                                                weight="regular"
                                                className={`mb-8 flex flex-col ${errors?.shippingAddress?.state ? "text-red-500" : "text-black"}`}
                                            >
                                                {customerInfo.pfasRestrictedProduct ? (
                                                    <span className="text-warning-red font_trimMonoPoc_Regular_12_120_uppercase">
                                                        {t(
                                                            langCode,
                                                            "restricted_product_message_form"
                                                        )}
                                                    </span>
                                                ) : (
                                                    <label htmlFor="shippingAddress.state">
                                                        {!errors?.shippingAddress
                                                            ?.state &&
                                                            t(
                                                                langCode,
                                                                fieldLabel.get("State") ??
                                                                    ""
                                                            )}
                                                        {errors?.shippingAddress?.state &&
                                                            t(
                                                                langCode,
                                                                errors?.shippingAddress
                                                                    ?.state?.message ?? ""
                                                            )}
                                                    </label>
                                                )}
                                            </Text>
                                            <div
                                                className="relative w-full"
                                                ref={el =>
                                                    (fieldRefs.current[
                                                        "shippingAddress.state"
                                                    ] = el)
                                                }
                                            >
                                                <select
                                                    className={`w-full appearance-none rounded-full border px-24 py-16 ${errors?.shippingAddress?.state ? "border-red-500" : ""} ${customerInfo.pfasRestrictedProduct ? "border-warning-red text-warning-red" : ""}`}
                                                    name="shippingAddress.state"
                                                    id="shippingAddress.state"
                                                    value={
                                                        customerInfo?.shippingAddress
                                                            ?.state
                                                    }
                                                    onChange={e => {
                                                        const value = e.target.value
                                                        updateCustomerInfo({
                                                            shippingAddress: {
                                                                ...customerInfo?.shippingAddress,
                                                                state: value
                                                            }
                                                        })
                                                        setValue(
                                                            "shippingAddress.state",
                                                            value
                                                        )
                                                        clearErrors(
                                                            "shippingAddress.state"
                                                        )
                                                        trigger("shippingAddress.state")
                                                    }}
                                                >
                                                    <option value="">
                                                        {t(langCode, "Select")}
                                                    </option>
                                                    {stateFields.map(state => (
                                                        <option
                                                            key={state.name}
                                                            value={state.name ?? ""}
                                                        >
                                                            {state.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-24 text-gray-700">
                                                    <IconChevronDown className="h-20 w-20" />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                                if (
                                    fieldName === "address1" &&
                                    field.required &&
                                    isLoaded
                                ) {
                                    return (
                                        <div className="col-span-2" key={fieldName}>
                                            <Text
                                                size="small"
                                                weight="regular"
                                                className="mb-8"
                                            >
                                                <label
                                                    htmlFor={fieldName}
                                                    className={`mb-8 ${
                                                        errors?.shippingAddress?.address1
                                                            ?.ref
                                                            ? "text-red-500"
                                                            : "text-black"
                                                    }`}
                                                >
                                                    {!errors?.shippingAddress?.address1 &&
                                                        t(
                                                            langCode,
                                                            fieldLabel.get(fieldName) ??
                                                                ""
                                                        )}
                                                    {errors?.shippingAddress?.address1 &&
                                                        t(
                                                            langCode,
                                                            errors?.shippingAddress
                                                                ?.address1?.message ?? ""
                                                        )}
                                                </label>
                                            </Text>
                                            <div
                                                ref={el =>
                                                    (fieldRefs.current[
                                                        `shippingAddress.${fieldName}`
                                                    ] = el)
                                                }
                                            >
                                                {loadError || forceRenderKey ? (
                                                    <TextField
                                                        {...register(
                                                            `shippingAddress.${fieldName}`,
                                                            fieldName === "address1"
                                                                ? {
                                                                      validate: value => {
                                                                          if (
                                                                              !isValidStreetName(
                                                                                  value
                                                                              )
                                                                          ) {
                                                                              return t(
                                                                                  langCode,
                                                                                  "Address cannot contain special characters"
                                                                              )
                                                                          }
                                                                          return true
                                                                      }
                                                                  }
                                                                : undefined
                                                        )}
                                                        type={"text"}
                                                        value={inputValue}
                                                        placeholder={t(
                                                            langCode,
                                                            fieldPlaceholder.get(
                                                                "address1"
                                                            ) ?? ""
                                                        )}
                                                        ref={inputRef}
                                                        onChange={e => {
                                                            handleInputChange(e)
                                                            trigger(
                                                                "shippingAddress.address1"
                                                            )
                                                        }}
                                                        theme="light"
                                                        id={"address1"}
                                                        name={"shippingAddress.address1"}
                                                        aria-label={"address1"}
                                                        required={field.required}
                                                        className={`${(errors?.shippingAddress as any)?.address1 ? "border-red-500" : ""}`}
                                                        onBlur={e => {
                                                            handleInputFocus(e)
                                                            trigger(
                                                                "shippingAddress.address1"
                                                            )
                                                        }}
                                                    />
                                                ) : (
                                                    <Autocomplete
                                                        onLoad={onLoad}
                                                        fields={["address_components"]}
                                                        options={autoCompleteOptions}
                                                        onPlaceChanged={onPlaceChanged}
                                                    >
                                                        <TextField
                                                            {...register(
                                                                `shippingAddress.${fieldName}`,
                                                                fieldName === "address1"
                                                                    ? {
                                                                          validate:
                                                                              value => {
                                                                                  if (
                                                                                      !isValidStreetName(
                                                                                          value
                                                                                      )
                                                                                  ) {
                                                                                      return t(
                                                                                          langCode,
                                                                                          "Address cannot contain special characters"
                                                                                      )
                                                                                  }
                                                                                  return true
                                                                              }
                                                                      }
                                                                    : undefined
                                                            )}
                                                            key={forceRenderKey}
                                                            type={"text"}
                                                            value={inputValue}
                                                            placeholder={t(
                                                                langCode,
                                                                fieldPlaceholder.get(
                                                                    "address1"
                                                                ) ?? ""
                                                            )}
                                                            ref={inputRef}
                                                            onChange={e => {
                                                                handleInputChange(e)
                                                                trigger(
                                                                    "shippingAddress.address1"
                                                                )
                                                            }}
                                                            theme="light"
                                                            id={"address1"}
                                                            name={
                                                                "shippingAddress.address1"
                                                            }
                                                            aria-label={"address1"}
                                                            required={field.required}
                                                            className={`${(errors?.shippingAddress as any)?.address1 ? "border-red-500" : ""}`}
                                                            onBlur={e => {
                                                                handleInputFocus(e)
                                                                trigger(
                                                                    "shippingAddress.address1"
                                                                )
                                                            }}
                                                            onKeyDown={e => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault()
                                                                    e.stopPropagation()
                                                                }
                                                            }}
                                                        />
                                                    </Autocomplete>
                                                )}
                                                <div className="pt-6 font-tradeLight text-12">
                                                    <div
                                                        className={
                                                            "mr-6 inline-flex h-16 w-16 select-none items-center justify-center rounded-lg border border-solid border-black text-center"
                                                        }
                                                    >
                                                        &#33;
                                                    </div>
                                                    {t(langCode, "Address-help-text")}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                                if (field.required || fieldName === "phoneNumber") {
                                    return (
                                        <div className="col-span-2" key={fieldName}>
                                            <Text
                                                size="small"
                                                weight="regular"
                                                className="mb-8"
                                            >
                                                {customerInfo.pfasRestrictedProduct &&
                                                fieldName === "zipCode" ? (
                                                    <span className="text-warning-red font_trimMonoPoc_Regular_12_120_uppercase">
                                                        {t(
                                                            langCode,
                                                            "restricted_product_message_form"
                                                        )}
                                                    </span>
                                                ) : (
                                                    <label
                                                        htmlFor={fieldName}
                                                        className={`mb-8 ${
                                                            (
                                                                errors?.shippingAddress as any
                                                            )?.[fieldName]?.ref
                                                                ? "text-red-500"
                                                                : "text-black"
                                                        }`}
                                                    >
                                                        {!(
                                                            errors?.shippingAddress as any
                                                        )?.[fieldName] &&
                                                            t(
                                                                langCode,
                                                                fieldLabel.get(
                                                                    fieldName
                                                                ) ?? ""
                                                            )}
                                                        {(
                                                            errors?.shippingAddress as any
                                                        )?.[fieldName] &&
                                                            t(
                                                                langCode,
                                                                (
                                                                    errors?.shippingAddress as any
                                                                )?.[fieldName]?.message ??
                                                                    ""
                                                            )}
                                                    </label>
                                                )}
                                            </Text>
                                            <div
                                                ref={el =>
                                                    (fieldRefs.current[
                                                        `shippingAddress.${fieldName}`
                                                    ] = el)
                                                }
                                            >
                                                <TextField
                                                    type={
                                                        fieldName === "phoneNumber"
                                                            ? "tel"
                                                            : "text"
                                                    }
                                                    {...register(
                                                        (`shippingAddress.` +
                                                            fieldName) as any,
                                                        fieldName === "zipCode"
                                                            ? {
                                                                  validate: value => {
                                                                      if (
                                                                          customerInfo
                                                                              .shippingAddress
                                                                              .country ===
                                                                              "US" &&
                                                                          (touchedFields
                                                                              .shippingAddress
                                                                              ?.zipCode ||
                                                                              value)
                                                                      ) {
                                                                          if (
                                                                              !/^\d{5}$/.test(
                                                                                  value
                                                                              )
                                                                          ) {
                                                                              return t(
                                                                                  langCode,
                                                                                  "us_zip_code_validation_message"
                                                                              )
                                                                          }

                                                                          if (
                                                                              !restrictedZip(
                                                                                  customerInfo
                                                                                      .shippingAddress
                                                                                      .state,
                                                                                  value
                                                                              )
                                                                          ) {
                                                                              return t(
                                                                                  langCode,
                                                                                  "zip_code_not_valid_for_state"
                                                                              )
                                                                          }
                                                                      }
                                                                      return true
                                                                  }
                                                              }
                                                            : undefined
                                                    )}
                                                    placeholder={t(
                                                        langCode,
                                                        fieldPlaceholder.get(fieldName) ??
                                                            ""
                                                    )}
                                                    theme="light"
                                                    id={fieldName}
                                                    name={`shippingAddress.${fieldName}`}
                                                    aria-label={fieldName}
                                                    required={field.required}
                                                    className={`${(errors?.shippingAddress as any)?.[fieldName]?.ref ? "border-red-500 text-red-500" : ""} ${customerInfo.pfasRestrictedProduct && fieldName === "zipCode" ? "border-warning-red text-warning-red focus:bg-white" : ""}`}
                                                    onChange={e => {
                                                        const value = e.target.value
                                                        updateCustomerInfo({
                                                            shippingAddress: {
                                                                ...customerInfo?.shippingAddress,
                                                                [fieldName]:
                                                                    e.target.value
                                                            }
                                                        })
                                                        switch (fieldName) {
                                                            case "firstName":
                                                                setValue(
                                                                    "shippingAddress.firstName",
                                                                    value
                                                                )
                                                                break
                                                            case "lastName":
                                                                setValue(
                                                                    "shippingAddress.lastName",
                                                                    value
                                                                )
                                                                break
                                                            case "phoneNumber":
                                                                setValue(
                                                                    "shippingAddress.phoneNumber",
                                                                    value
                                                                )
                                                                break
                                                            case "address1":
                                                                setValue(
                                                                    "shippingAddress.address1",
                                                                    value
                                                                )
                                                                break
                                                            case "address2":
                                                                setValue(
                                                                    "shippingAddress.address2",
                                                                    value
                                                                )
                                                                break
                                                            case "city":
                                                                setValue(
                                                                    "shippingAddress.city",
                                                                    value
                                                                )
                                                                break
                                                            case "zipCode":
                                                                setValue(
                                                                    "shippingAddress.zipCode",
                                                                    value
                                                                )
                                                                if (
                                                                    customerInfo
                                                                        .shippingAddress
                                                                        .country === "US"
                                                                ) {
                                                                    trigger(
                                                                        "shippingAddress.zipCode"
                                                                    )
                                                                }
                                                                break
                                                            default:
                                                                break
                                                        }
                                                        trigger(
                                                            `shippingAddress.${fieldName as keyof CheckoutFormData["shippingAddress"]}`
                                                        )
                                                        clearErrors(
                                                            `shippingAddress.${fieldName as keyof CheckoutFormData["shippingAddress"]}`
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )
                                } else {
                                    return undefined
                                }
                            })}
                    </div>
                    <div className="mb-32 md:mb-48" ref={billingFormRef}>
                        <StoryText size="size2" className="mb-24">
                            {t(langCode, "BILLING ADDRESS")}
                        </StoryText>
                        <div className="grid gap-16 rounded-[4px] border px-16 py-24">
                            <div>
                                <div className="mb-24">
                                    <RadioButton
                                        name="differentBillingAddress"
                                        options={[
                                            {
                                                label: t(
                                                    langCode,
                                                    "Same as shipping address"
                                                ),
                                                value: "false"
                                            },
                                            {
                                                label: t(
                                                    langCode,
                                                    "Use a different billing address"
                                                ),
                                                value: "true"
                                            }
                                        ]}
                                        selectedValue={
                                            customerInfo?.differentShippingAddress
                                                ? "true"
                                                : "false"
                                        }
                                        theme="light"
                                        handleSelection={handleDifferentBillingAddress}
                                    />
                                </div>
                            </div>

                            {customerInfo?.differentShippingAddress && (
                                <BillingFields
                                    billingFields={billingFields}
                                    fieldLabel={fieldLabel}
                                    errors={errors}
                                    setValue={setValue}
                                    fieldPlaceholder={fieldPlaceholder}
                                    updateCustomerInfo={updateCustomerInfo}
                                    customerInfo={customerInfo}
                                    register={register}
                                    countries={countries}
                                    fieldRefs={fieldRefs}
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex w-full flex-col-reverse justify-center gap-16 md:flex-row md:justify-between md:gap-0">
                        <div className="w-full">
                            <DynamicLink
                                href={"/"}
                                className="flex items-center justify-center gap-8 md:justify-start"
                                prefetch={false}
                                aria-label={t(langCode, "Return to Shop")}
                            >
                                <IconArrowLeft className="h-16 w-16" />
                                <Text size="medium" weight="regular" color="black">
                                    {t(langCode, "Return to Shop")}
                                </Text>
                            </DynamicLink>
                        </div>
                        <div className="flex w-full justify-end">
                            <LoadingButton
                                langCode={langCode}
                                title="Continue to shipping"
                                isLoading={isLoading}
                                disabled={customerInfo.pfasRestrictedProduct}
                                isCheckout={true}
                            />
                        </div>
                    </div>
                </form>
            )}
        </>
    )
}

export default Information
